import { useMemo, useRef, type FC } from 'react'
import Editor, { Monaco } from '@monaco-editor/react'
import { ConfigSection } from '~/features/webchat/components'
import { webchatClasses } from '@botpress/webchat'
import { getUseConfigStore } from '~/features/webchat/stores/useConfigStore'
import { useLocalStore } from '~/stores'

type StandaloneEditor = ReturnType<Monaco['editor']['create']>

type Props = {
  workspaceId: string
  botId: string
}

export const CustomStylesSection: FC<Props> = ({ workspaceId, botId }) => {
  const useConfigStore = useMemo(() => getUseConfigStore({ botId, workspaceId }), [botId, workspaceId])

  const customStylesheet = useConfigStore((state) => state.customStyleSheet)
  const setCustomStylesheet = useConfigStore((state) => state.setCustomStyleSheet)

  const darkMode = useLocalStore((s) => s.theme)
  const editorRef = useRef<StandaloneEditor | null>(null)

  const handleEditorDidMount = (editor: StandaloneEditor, monaco: Monaco) => {
    editorRef.current = editor

    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      formatEditor()
    })

    monaco.languages.css.cssDefaults.setOptions({
      validate: true,
    })

    monaco.languages.registerCompletionItemProvider('css', {
      provideCompletionItems: () => {
        const suggestions: any[] = []

        function traverseClasses(obj: Record<string, any>) {
          for (const key in obj) {
            const value = obj[key]
            if (value && typeof value === 'object') {
              traverseClasses(value)
            } else if (key === 'className' && typeof value === 'string') {
              const classes = value.split(' ')
              for (const className of classes) {
                if (!suggestions.some((s) => s.label === `.${className}`)) {
                  suggestions.push({
                    label: `.${className}`,
                    kind: monaco.languages.CompletionItemKind.Class,
                    insertText: `.${className}`,
                  })
                }
              }
            }
          }
        }
        traverseClasses(webchatClasses)

        return { suggestions }
      },
    })
  }

  const formatEditor = () => {
    editorRef.current?.getAction('editor.action.formatDocument')?.run()
  }

  return (
    <ConfigSection title="Styles" description="Custom styles for your bot.">
      <div className="rounded-sm border border-neutral-300 py-1">
        <Editor
          height={400}
          language="css"
          theme={darkMode === 'dark' ? 'vs-dark' : 'light'}
          defaultValue="/* Your CSS styles here */"
          value={customStylesheet}
          onChange={setCustomStylesheet}
          onMount={handleEditorDidMount}
        />
      </div>
    </ConfigSection>
  )
}
